import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { reportsApi } from "@/lib/api/reports";
import { leavesApi } from "@/lib/api/leaves";
import { attendanceApi } from "@/lib/api/attendance";
import { expenseApi } from "@/lib/api/expense";
import { helpdeskApi } from "@/lib/api/helpdesk";
import { documentsApi } from "@/lib/api/documents";
import { performanceApi } from "@/lib/api/performance";
import { employeeLifecycleApi } from "@/lib/api/employeeLifecycle";
import { recruitmentApi } from "@/lib/api/recruitment";
import { qk } from "@/lib/query-client";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { Icon } from "@/components/layout/Icon";
import { cn } from "@/lib/utils/cn";
import { can, canAny, useAuthStore } from "@/stores/auth-store";
import { useFeatureEnabled } from "@/lib/features";

interface ActionCenterItem {
  key: string;
  icon: string;
  title: string;
  description: string;
  tone: "amber" | "red" | "indigo" | "emerald";
  cta: "Review" | "View" | "Complete" | "Continue";
  to: string;
}

interface FailedSource {
  key: string;
  refetch: () => void;
}

const TONE_CLASSES: Record<ActionCenterItem["tone"], string> = {
  amber: "bg-amber-50 text-amber-600",
  red: "bg-red-50 text-red-600",
  indigo: "bg-indigo-50 text-indigo-600",
  emerald: "bg-emerald-50 text-emerald-600",
};

/** Dynamic, RBAC-aware "what needs me right now" list - the redesigned
 * replacement for the old static "Needs your attention" card. Every item is
 * gated on the same featureEnabled && can(perm) pattern the rest of the
 * dashboard uses, and every query is independently `enabled` so a role only
 * ever fetches what it could actually see - no query fires for a module the
 * caller lacks permission for. */
export function ActionCenter({ className = "mb-6" }: { className?: string }) {
  const user = useAuthStore((s) => s.user);

  const leaveEnabled = useFeatureEnabled("leave_management");
  const attendanceEnabled = useFeatureEnabled("attendance_management");
  const payrollEnabled = useFeatureEnabled("payroll_management");
  const expenseEnabled = useFeatureEnabled("expense_management");
  const helpdeskEnabled = useFeatureEnabled("hr_helpdesk");
  const documentEnabled = useFeatureEnabled("document_management");
  const performanceEnabled = useFeatureEnabled("performance_management");
  const employeeMgmtEnabled = useFeatureEnabled("employee_management");
  const onboardingEnabled = useFeatureEnabled("employee_onboarding");
  const recruitmentEnabled = useFeatureEnabled("recruitment");
  const geoFencingEnabled = useFeatureEnabled("geo_fencing");

  // ---- gates ----
  const canApproveLeave = leaveEnabled && can("leave.approve");
  const canApproveLeaveFinal = leaveEnabled && can("leave.approve.final");
  // Plain self-service leave only, for a caller who isn't also an approver -
  // an approver already gets their own "pending decisions from me" item
  // below and doesn't need a second "my own leave" item competing for space.
  const canSeeMyLeave = leaveEnabled && can("leave.read") && !canApproveLeave && !canApproveLeaveFinal;
  const canSeeAttendanceExceptions = attendanceEnabled && can("attendance.read");
  const canSeePayroll = payrollEnabled && can("payroll.read");
  const canApproveExpenseTeam = expenseEnabled && can("expense.view_team") && can("expense.approve");
  const canApproveExpenseReports = expenseEnabled && can("expense.view_reports") && can("expense.approve");
  const canSeeHelpdesk = helpdeskEnabled && canAny("helpdesk.view_team", "helpdesk.view_all", "helpdesk.view_payroll");
  // documents.reports/* is gated on document.view_reports, not the plain
  // document.view an ordinary Employee holds for their own documents - a
  // wrong gate here 403s in the background for every Employee-tier login.
  const canSeeDocumentReports = documentEnabled && can("document.view_reports");
  // Note: there is no self-service "my documents" endpoint a plain Employee
  // can call - /document-tasks looks self-scoped by name but is actually
  // HR/Tenant-Admin-only server-side (DocumentService.MyTasks guards on
  // isTenantAdmin/isHR, independent of the route's document.view permission
  // gate). Confirmed live rather than assumed. The spec's "Employee: my
  // documents" example has no real backend to back it - disclosed cut,
  // matching "Incomplete Employee Profiles" - not building a misleading
  // item against data that would 403 for the exact audience it's meant for.
  const canSeePerformance = performanceEnabled && can("performance.dashboard.view");
  const canSeeOnboarding = employeeMgmtEnabled && can("employee.lifecycle.read");
  // The self-service "my onboarding" record additionally requires an actual
  // employee link - a pure admin/HR login (Tenant Admin, most HR accounts)
  // holds employee.lifecycle.read but has no Employee record of their own,
  // and GET /employee/self/onboarding 403s for them without this guard
  // (mirrors App.tsx's own requireEmployeeLink gate on the /ess/lifecycle
  // route this item links to).
  const canSeeMyOnboarding = canSeeOnboarding && !!user?.employee_id;
  // The in-progress list is GET /onboardings, gated on employee_onboarding
  // as well (router.go) - with that toggle off it would 403 into an error item.
  const canSeeOnboardingsInProgress = canSeeOnboarding && onboardingEnabled;
  const canSeeRecruitment = recruitmentEnabled && can("recruitment.dashboard.view");
  const canSeeGeoFencing = geoFencingEnabled && !user?.is_super_admin && can("report.read");

  // ---- queries - each owns its own enabled gate, data and error state, so
  // a failure in any one of these can never block the others or bubble up
  // to DashboardPage's own top-level error state (which only ever watches
  // its own qk.reports.dashboard query). ----
  const dashboardQuery = useQuery({ queryKey: qk.reports.dashboard, queryFn: reportsApi.dashboard });
  const geoSnapshotQuery = useQuery({
    queryKey: qk.reports.geoFenceSummary,
    queryFn: () => reportsApi.geoFenceSummary(),
    enabled: canSeeGeoFencing,
  });
  const myLeaveQuery = useQuery({
    queryKey: qk.leaves.list({ per_page: 20 }),
    queryFn: () => leavesApi.list({ per_page: 20 }),
    enabled: canSeeMyLeave,
  });
  const managerLeaveQuery = useQuery({
    queryKey: qk.leaves.list({ status: "pending_manager", per_page: 1 }),
    queryFn: () => leavesApi.list({ status: "pending_manager", per_page: 1 }),
    enabled: canApproveLeave,
  });
  const hrLeaveQuery = useQuery({
    queryKey: qk.leaves.list({ status: "pending_hr", per_page: 1 }),
    queryFn: () => leavesApi.list({ status: "pending_hr", per_page: 1 }),
    enabled: canApproveLeaveFinal,
  });
  const attendanceExceptionsQuery = useQuery({
    queryKey: qk.attendance.exceptions({ per_page: 1 }),
    queryFn: () => attendanceApi.exceptions({ per_page: 1 }),
    enabled: canSeeAttendanceExceptions,
  });
  const expenseTeamQuery = useQuery({
    queryKey: qk.expense.team({ status: "pending_approval", per_page: 1 }),
    queryFn: () => expenseApi.team({ status: "pending_approval", per_page: 1 }),
    enabled: canApproveExpenseTeam,
  });
  // Reuses the same query the KPI row already fires for Total Expense when
  // this caller is Finance-tier (view_reports + approve) - TanStack Query
  // dedupes by key, so this is not a second network request for that role.
  const expenseSummaryQuery = useQuery({
    queryKey: qk.expense.summary,
    queryFn: expenseApi.summary,
    enabled: canApproveExpenseReports,
  });
  const helpdeskSummaryQuery = useQuery({
    queryKey: qk.helpdesk.summary({}),
    queryFn: () => helpdeskApi.summary({}),
    enabled: canSeeHelpdesk,
  });
  const documentsExpiringQuery = useQuery({
    queryKey: qk.document.reportExpiring(30),
    queryFn: () => documentsApi.reportExpiring(30),
    enabled: canSeeDocumentReports,
  });
  const performanceQuery = useQuery({
    queryKey: qk.performance.dashboard,
    queryFn: performanceApi.dashboard,
    enabled: canSeePerformance,
  });
  const myOnboardingQuery = useQuery({
    queryKey: qk.onboarding.my,
    queryFn: employeeLifecycleApi.myOnboarding,
    enabled: canSeeMyOnboarding,
    retry: false,
  });
  const onboardingInProgressQuery = useQuery({
    queryKey: qk.onboarding.list({ status: "in_progress", per_page: 1 }),
    queryFn: () => employeeLifecycleApi.listOnboardings({ status: "in_progress", per_page: 1 }),
    enabled: canSeeOnboardingsInProgress,
  });
  const recruitmentQuery = useQuery({
    queryKey: qk.recruitment.dashboard,
    queryFn: () => recruitmentApi.dashboard(),
    enabled: canSeeRecruitment,
  });

  const { items, failedSources } = useMemo(() => {
    const items: ActionCenterItem[] = [];
    const failedSources: FailedSource[] = [];
    const push = (item: ActionCenterItem) => items.push(item);
    const fail = (key: string, q: { refetch: () => unknown }) => failedSources.push({ key, refetch: () => q.refetch() });

    if (dashboardQuery.data) {
      const { previous } = dashboardQuery.data;
      if (canSeePayroll) {
        if (!previous?.payroll) {
          push({
            key: "payroll-missing", icon: "wallet", tone: "amber", cta: "Complete",
            title: "Payroll not yet generated",
            description: "Generate a payroll run for the previous period",
            to: "/payroll/runs",
          });
        } else if (previous.payroll.status !== "locked" && previous.payroll.status !== "paid" && previous.payroll.status !== "completed") {
          push({
            key: "payroll-pending", icon: "wallet", tone: "amber", cta: "Complete",
            title: `Payroll is ${previous.payroll.status.replace(/_/g, " ")}`,
            description: "Needs review before it can be finalised",
            to: "/payroll/runs",
          });
        }
      }
    } else if (dashboardQuery.isError) {
      fail("dashboard", dashboardQuery);
    }

    if (canSeeGeoFencing) {
      if (geoSnapshotQuery.data) {
        const n = geoSnapshotQuery.data.outside_fence_today;
        if (n > 0) {
          push({
            key: "geofence", icon: "map-pin", tone: "red", cta: "View",
            title: `${n} outside-fence check-in${n === 1 ? "" : "s"} today`,
            description: "Review geo-fencing activity",
            to: "/geo-fencing",
          });
        }
      } else if (geoSnapshotQuery.isError) {
        fail("geofence", geoSnapshotQuery);
      }
    }

    if (canSeeMyLeave) {
      if (myLeaveQuery.data) {
        const pending = myLeaveQuery.data.items.filter((l) => l.status === "pending_manager" || l.status === "pending_hr").length;
        if (pending > 0) {
          push({
            key: "my-leave", icon: "calendar-check", tone: "indigo", cta: "View",
            title: `${pending} leave request${pending === 1 ? "" : "s"} awaiting a decision`,
            description: "Your submitted leave is still pending",
            to: "/leaves",
          });
        }
      } else if (myLeaveQuery.isError) {
        fail("my-leave", myLeaveQuery);
      }
    }

    if (canApproveLeave) {
      if (managerLeaveQuery.data) {
        const n = managerLeaveQuery.data.meta?.total ?? 0;
        if (n > 0) {
          push({
            key: "team-leave", icon: "calendar-check", tone: "indigo", cta: "Review",
            title: `${n} leave approval${n === 1 ? "" : "s"} pending`,
            description: "Team members are waiting for your decision",
            to: "/leaves?status=pending_manager",
          });
        }
      } else if (managerLeaveQuery.isError) {
        fail("team-leave", managerLeaveQuery);
      }
    }

    if (canApproveLeaveFinal) {
      if (hrLeaveQuery.data) {
        const n = hrLeaveQuery.data.meta?.total ?? 0;
        if (n > 0) {
          push({
            key: "hr-leave", icon: "calendar-check", tone: "indigo", cta: "Review",
            title: `${n} leave request${n === 1 ? "" : "s"} awaiting final approval`,
            description: "HR sign-off needed to complete these requests",
            to: "/leaves?status=pending_hr",
          });
        }
      } else if (hrLeaveQuery.isError) {
        fail("hr-leave", hrLeaveQuery);
      }
    }

    if (canSeeAttendanceExceptions) {
      if (attendanceExceptionsQuery.data) {
        const n = attendanceExceptionsQuery.data.meta?.total ?? 0;
        if (n > 0) {
          push({
            key: "attendance-exceptions", icon: "alert-triangle", tone: "amber", cta: "Review",
            title: `${n} attendance exception${n === 1 ? "" : "s"}`,
            description: "Late arrivals & missing punches to review",
            to: "/attendance/exceptions",
          });
        }
      } else if (attendanceExceptionsQuery.isError) {
        fail("attendance-exceptions", attendanceExceptionsQuery);
      }
    }

    if (canApproveExpenseTeam) {
      if (expenseTeamQuery.data) {
        const n = expenseTeamQuery.data.meta?.total ?? 0;
        if (n > 0) {
          push({
            key: "team-expense", icon: "credit-card", tone: "amber", cta: "Review",
            title: `${n} expense approval${n === 1 ? "" : "s"} pending`,
            description: "Team expense claims are waiting for your decision",
            to: "/expenses/team?status=pending_approval",
          });
        }
      } else if (expenseTeamQuery.isError) {
        fail("team-expense", expenseTeamQuery);
      }
    }

    if (canApproveExpenseReports) {
      if (expenseSummaryQuery.data) {
        const n = expenseSummaryQuery.data.pending_approval.count;
        if (n > 0) {
          push({
            key: "reports-expense", icon: "credit-card", tone: "amber", cta: "Review",
            title: `${n} expense claim${n === 1 ? "" : "s"} pending approval`,
            description: "Awaiting your review before payment",
            to: "/expenses/reports?status=pending_approval",
          });
        }
      } else if (expenseSummaryQuery.isError) {
        fail("reports-expense", expenseSummaryQuery);
      }
    }

    if (canSeeHelpdesk) {
      if (helpdeskSummaryQuery.data) {
        const n = (helpdeskSummaryQuery.data.open ?? 0) + (helpdeskSummaryQuery.data.in_progress ?? 0);
        if (n > 0) {
          push({
            key: "helpdesk", icon: "help-circle", tone: "indigo", cta: "View",
            title: `${n} helpdesk ticket${n === 1 ? "" : "s"} open`,
            description: "Employee requests waiting for a response",
            to: "/helpdesk/tickets?status=open",
          });
        }
      } else if (helpdeskSummaryQuery.isError) {
        fail("helpdesk", helpdeskSummaryQuery);
      }
    }

    if (canSeeDocumentReports) {
      if (documentsExpiringQuery.data) {
        const n = documentsExpiringQuery.data.count;
        if (n > 0) {
          push({
            key: "documents-expiring", icon: "file-text", tone: "amber", cta: "View",
            title: `${n} document${n === 1 ? "" : "s"} expiring soon`,
            description: "Renew before they lapse",
            to: "/documents/reports?tab=expiring",
          });
        }
      } else if (documentsExpiringQuery.isError) {
        fail("documents-expiring", documentsExpiringQuery);
      }
    }

    if (canSeePerformance) {
      if (performanceQuery.data) {
        const self = performanceQuery.data.pending_self_reviews ?? 0;
        const manager = performanceQuery.data.pending_manager_reviews ?? 0;
        if (self > 0) {
          push({
            key: "performance-self", icon: "target", tone: "indigo", cta: "Complete",
            title: `${self} self-review${self === 1 ? "" : "s"} due`,
            description: "Complete your performance self-assessment",
            to: "/performance/reviews",
          });
        }
        if (manager > 0) {
          push({
            key: "performance-manager", icon: "target", tone: "indigo", cta: "Review",
            title: `${manager} team review${manager === 1 ? "" : "s"} due`,
            description: "Your team is waiting on your review",
            to: "/performance/reviews",
          });
        }
      } else if (performanceQuery.isError) {
        fail("performance", performanceQuery);
      }
    }

    if (canSeeOnboarding) {
      // A caller with no active onboarding record gets a 404 here - the
      // common, expected case for anyone not currently onboarding, not a
      // real failure, so it's never added to failedSources.
      if (myOnboardingQuery.data) {
        const pending = myOnboardingQuery.data.checklist.filter((t) => !t.done).length;
        if (pending > 0) {
          push({
            key: "my-onboarding", icon: "clipboard-list", tone: "emerald", cta: "Continue",
            title: `${pending} onboarding task${pending === 1 ? "" : "s"} left`,
            description: "Finish your onboarding checklist",
            to: "/ess/lifecycle",
          });
        }
      }

      if (!canSeeOnboardingsInProgress) {
        // toggled off: no item, and not a failure
      } else if (onboardingInProgressQuery.data) {
        const n = onboardingInProgressQuery.data.meta?.total ?? 0;
        if (n > 0) {
          push({
            key: "onboarding-in-progress", icon: "clipboard-list", tone: "emerald", cta: "View",
            title: `${n} onboarding${n === 1 ? "" : "s"} in progress`,
            description: "New hires currently going through onboarding",
            to: "/onboarding-probation",
          });
        }
      } else if (onboardingInProgressQuery.isError) {
        fail("onboarding-in-progress", onboardingInProgressQuery);
      }
    }

    if (canSeeRecruitment) {
      if (recruitmentQuery.data) {
        const d = recruitmentQuery.data;
        if (d.interviews_today > 0) {
          push({
            key: "recruitment-interviews", icon: "users", tone: "indigo", cta: "View",
            title: `${d.interviews_today} interview${d.interviews_today === 1 ? "" : "s"} today`,
            description: "Scheduled interviews happening today",
            to: "/recruitment/interviews",
          });
        }
      } else if (recruitmentQuery.isError) {
        fail("recruitment", recruitmentQuery);
      }
    }

    return { items, failedSources };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dashboardQuery.data, dashboardQuery.isError, canSeePayroll,
    geoSnapshotQuery.data, geoSnapshotQuery.isError, canSeeGeoFencing,
    myLeaveQuery.data, myLeaveQuery.isError, canSeeMyLeave,
    managerLeaveQuery.data, managerLeaveQuery.isError, canApproveLeave,
    hrLeaveQuery.data, hrLeaveQuery.isError, canApproveLeaveFinal,
    attendanceExceptionsQuery.data, attendanceExceptionsQuery.isError, canSeeAttendanceExceptions,
    expenseTeamQuery.data, expenseTeamQuery.isError, canApproveExpenseTeam,
    expenseSummaryQuery.data, expenseSummaryQuery.isError, canApproveExpenseReports,
    helpdeskSummaryQuery.data, helpdeskSummaryQuery.isError, canSeeHelpdesk,
    documentsExpiringQuery.data, documentsExpiringQuery.isError, canSeeDocumentReports,
    performanceQuery.data, performanceQuery.isError, canSeePerformance,
    myOnboardingQuery.data, canSeeMyOnboarding, onboardingInProgressQuery.data, onboardingInProgressQuery.isError, canSeeOnboarding, canSeeOnboardingsInProgress,
    recruitmentQuery.data, recruitmentQuery.isError, canSeeRecruitment,
  ]);

  return (
    <Card interactive className={className}>
      <CardHeader>
        <CardTitle>Action Center</CardTitle>
        <p className="text-xs text-slate-500">Tasks and items that need your attention</p>
      </CardHeader>
      <CardBody className={items.length > 0 ? "p-0" : undefined}>
        {dashboardQuery.isLoading ? (
          <div className="space-y-3 p-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-9 w-9 rounded-lg" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-1/2" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Icon name="check-circle" className="h-7 w-7" />}
            title="You're all caught up!"
            description="No actions require your attention right now."
          />
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((item) => (
              <li key={item.key}>
                <Link
                  to={item.to}
                  className="flex items-center gap-3 px-5 py-3.5 transition-colors duration-200 ease-out hover:bg-primary-50/40"
                >
                  <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", TONE_CLASSES[item.tone])}>
                    <Icon name={item.icon} className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-800">{item.title}</p>
                    <p className="truncate text-xs text-slate-500">{item.description}</p>
                  </div>
                  <span className="shrink-0 text-xs font-medium text-primary-700">{item.cta}</span>
                  <Icon name="chevron-down" className="h-4 w-4 shrink-0 -rotate-90 text-slate-300" />
                </Link>
              </li>
            ))}
          </ul>
        )}
        {failedSources.length > 0 && (
          <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-amber-50/50 px-5 py-2.5 text-xs text-amber-700">
            <span>Some items couldn't load ({failedSources.length}).</span>
            <button
              type="button"
              onClick={() => failedSources.forEach((f) => f.refetch())}
              className="font-medium underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
